/**
 * LuviaCore — the data/auth layer behind the legacy dashboard UI (public/legacy/app.js).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EMPLOYEE-PC SYNC FIX — what was wrong and what this file changes
 *
 *  Before: AUTH.users (IDs + plaintext passwords), CONFIG (employee master, menus,
 *  API keys, the Worker URL) and all work data were read from and written to
 *  window.localStorage. localStorage belongs to one browser profile on one PC, so
 *  whatever the admin configured never existed on an employee's PC — their browser
 *  only had the hard-coded seed accounts, hence "ID or password is incorrect".
 *
 *  After:  1. Sign-in goes through Supabase Auth (email + password, server-verified).
 *          2. On every page load, init() pulls settings, members/permissions and all
 *             module records for the user's workspace from Postgres (RLS-filtered).
 *          3. A Realtime channel pushes later changes from any PC into this one.
 *          4. Every write goes to Postgres first; memory is only a cache of the server.
 *  Nothing shared lives in localStorage any more — only per-browser UI preferences.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  asJsonObject,
  asString,
  createMember,
  createRecordsRepository,
  DataError,
  deleteAudit,
  deleteMember,
  diffTopLevel,
  fetchAudit,
  fetchSecrets,
  fetchSettings,
  fetchTaskScores,
  getEnv,
  getSession,
  getSupabase,
  insertAudit,
  isEmptyPatch,
  isJsonObject,
  isValidEmail,
  jsonEqual,
  listAppModules,
  listMembers,
  listMyWorkspaces,
  normalizeEmail,
  onAuthStateChange,
  parseGrants,
  parseMenus,
  grantsToJson,
  patchSecrets,
  patchSettings,
  companyUrl,
  preferredCompanySlug,
  rememberCompanySlug,
  resolveWorkspace,
  saveMembers,
  sendPasswordResetEmail,
  setMemberActive,
  setMemberPassword,
  setMemberRole,
  signInWithPassword,
  signInWithGoogle,
  signOut,
  uploadFile,
  signedFileUrl,
  removeFile,
  copyFile,
  SignInError,
  subscribeWorkspace,
  toDataError,
  toJson,
  toJsonObject,
  upsertTaskScore,
  type Json,
  type JsonObject,
  type MemberRole,
  type RealtimeStatus,
  type SaveMemberInput,
  type RecordDoc,
  type RecordsRepository,
  type TaskScoreMap,
  type WorkspaceContext,
  type WorkspaceMember,
  type WorkspaceScope,
} from '../lib/supabase';
import {
  LegacyWriteError,
  type CoreInfo,
  type LegacyAuditRow,
  type LegacyLocalData,
  type LegacyUser,
  type LuviaCoreApi,
  type PullResult,
  type RemoteChange,
  type SignInResult,
} from './types';

/** API tokens: stored in workspace_secrets (admin-only RLS), never sent to employees. */
const SECRET_KEYS = ['GH_TOKEN', 'CF_API_TOKEN', 'SECRET_TOKEN', 'API_KEY', 'GEMINI_API_KEY'] as const;
const CONNECTOR_KEYS_FIELD = 'CONNECTOR_KEYS';
/** Per-browser preferences: kept in localStorage, not shared across PCs. */
const LOCAL_PREF_KEYS = ['SYNC_MODE'] as const;
/** Values owned by the code/build, never persisted as settings. */
const CODE_OWNED_KEYS = ['VERSION', 'APP_KEY', 'SALT', 'API_BASE_URL', 'REALTIME_URL', 'POLL_INTERVAL_MS'] as const;
/** Keys the pre-Supabase build kept in localStorage (see stashing in init()). */
const LEGACY_SECURITY_KEYS = ['users', 'session'] as const;
const LEGACY_DATA_KEYS = ['config', 'pending', 'seeded'] as const;

const ROLES: readonly MemberRole[] = ['admin', 'manager', 'employee'];

function isRole(value: unknown): value is MemberRole {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

class LegacyCore implements LuviaCoreApi {
  readonly appKey: string;

  private ctx: WorkspaceContext | null = null;
  private repo: RecordsRepository | null = null;
  private settingsServer: JsonObject = {};
  private secretsServer: JsonObject = {};
  /** Stage 5 scores. Empty for employees: task_scores RLS returns them no rows. */
  private taskScores: TaskScoreMap = {};
  private members: WorkspaceMember[] = [];
  private moduleList: string[] = [];
  private readonly cache = new Map<string, Map<string, RecordDoc>>();
  private readonly cursors = new Map<string, string>();
  private auditRows: LegacyAuditRow[] = [];
  private boot: string | null = null;
  private readonly listeners: Array<(change: RemoteChange) => void> = [];
  private unsubscribeRealtime: (() => void) | null = null;
  private realtimeState: RealtimeStatus = 'idle';
  private realtimeWasLive = false;
  private settingsQueue: Promise<void> = Promise.resolve();

  constructor() {
    this.appKey = getEnv().appKey;
  }

  // ───────────────────────────── lifecycle ─────────────────────────────

  async init(): Promise<void> {
    this.purgeLegacySecurityKeys();

    let session;
    try {
      session = await getSession();
    } catch (err: unknown) {
      this.boot = `network:${errorMessage(err)}`;
      return;
    }
    if (!session) return;

    try {
      const ctx = await resolveWorkspace(session.user.id, this.appKey, preferredCompanySlug());
      if (!ctx) {
        await signOut();
        this.boot = 'no_workspace';
        return;
      }
      await this.hydrate(ctx);
    } catch (err: unknown) {
      this.ctx = null;
      this.boot = `network:${errorMessage(err)}`;
      return;
    }

    this.watchAuthState();
  }

  private myWorkspaces: Array<{ id: string; slug: string; name: string }> = [];

  private async loadMyWorkspaces(userId: string): Promise<void> {
    this.myWorkspaces = await listMyWorkspaces(userId).catch(() => []);
  }

  private authStateWatched = false;
  private watchAuthState(): void {
    if (this.authStateWatched) return;
    this.authStateWatched = true;
    onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') this.emit({ kind: 'signed_out' });
    });
  }

  private async hydrate(ctx: WorkspaceContext): Promise<void> {
    // Pin the company we actually landed in (a stale ?company= or a revoked membership falls
    // back to the first membership), so a reload or a shared link reopens this same company.
    rememberCompanySlug(ctx.workspace.slug);
    void this.loadMyWorkspaces(ctx.member.userId);
    const scope: WorkspaceScope = { workspaceId: ctx.workspaceId, appKey: ctx.appKey };
    const role = ctx.member.role;
    const repo = createRecordsRepository(scope);

    // Records can't be listed until we know the module names, but nothing else below depends on
    // modules — so kick the module-names fetch off immediately and chain the (slow) per-module
    // records fetch onto it, running that chain alongside settings/secrets/members/audit/scores
    // instead of waiting for all of those to finish first.
    const recordsPromise = listAppModules(scope.appKey).then(async (modules) => {
      const moduleNames = modules.map((m) => m.module);
      const lists = await Promise.all(moduleNames.map(async (module) => [module, await repo.list(module)] as const));
      return { moduleNames, lists };
    });

    const [settings, secrets, members, audit, scores, { moduleNames, lists }] = await Promise.all([
      fetchSettings(scope),
      role === 'admin' ? fetchSecrets(scope) : Promise.resolve<JsonObject>({}),
      listMembers(scope),
      role === 'admin' || role === 'manager'
        ? fetchAudit(scope).catch(() => [])
        : Promise.resolve([]),
      role === 'admin' || role === 'manager'
        ? fetchTaskScores(scope).catch(() => ({}) as TaskScoreMap)
        : Promise.resolve<TaskScoreMap>({}),
      recordsPromise,
    ]);

    this.ctx = ctx;
    this.repo = repo;
    this.settingsServer = settings;
    this.secretsServer = secrets;
    this.taskScores = scores;
    this.members = members;
    this.moduleList = moduleNames;
    this.cache.clear();
    this.cursors.clear();
    for (const [module, docs] of lists) {
      const map = new Map<string, RecordDoc>();
      let cursor = '';
      for (const doc of docs) {
        map.set(doc.id, doc);
        if (doc.updatedAt > cursor) cursor = doc.updatedAt;
      }
      this.cache.set(module, map);
      this.cursors.set(module, cursor);
    }
    this.auditRows = audit.map((entry) => ({
      id: entry.id,
      at: entry.at,
      who: entry.actorEmail ?? '-',
      whoName: entry.actorName ?? entry.actorEmail ?? '-',
      action: entry.action,
      module: entry.module,
      targetId: entry.targetId,
      label: entry.label,
      before: entry.before,
      after: entry.after,
    }));
  }

  private get scope(): WorkspaceScope {
    if (!this.ctx) throw new DataError('unauthenticated', 'Not signed in.');
    return { workspaceId: this.ctx.workspaceId, appKey: this.ctx.appKey };
  }

  private get me(): WorkspaceMember | null {
    if (!this.ctx) return null;
    return this.members.find((m) => m.userId === this.ctx?.member.userId) ?? this.ctx.member;
  }

  private emit(change: RemoteChange): void {
    for (const listener of this.listeners) {
      try {
        listener(change);
      } catch (err: unknown) {
        console.error('[LuviaCore] change listener failed', err);
      }
    }
  }

  onRemoteChange(handler: (change: RemoteChange) => void): void {
    this.listeners.push(handler);
  }

  /**
   * Stage 5 scores, keyed by task id. Employees always see {} — the rows are withheld
   * by RLS, not filtered here — so the legacy UI can render straight from this map.
   */
  scores(): TaskScoreMap {
    return { ...this.taskScores };
  }

  async saveScore(taskId: string, score: number, comment: string): Promise<void> {
    const me = this.me;
    if (!me || (me.role !== 'admin' && me.role !== 'manager')) {
      throw new DataError('forbidden', 'Only an administrator or manager can score a task.');
    }
    const saved = await upsertTaskScore(this.scope, me.userId, {
      taskId,
      score,
      comment,
      byName: me.displayName || me.email || '',
    });
    this.taskScores = { ...this.taskScores, [taskId]: saved };
  }

  async refreshScores(): Promise<void> {
    const me = this.me;
    if (!me || (me.role !== 'admin' && me.role !== 'manager')) {
      this.taskScores = {};
      return;
    }
    this.taskScores = await fetchTaskScores(this.scope).catch(() => this.taskScores);
  }

  info(): CoreInfo {
    return {
      supabaseUrl: getEnv().supabaseUrl,
      appKey: this.appKey,
      workspace: this.ctx ? { ...this.ctx.workspace } : null,
      workspaces: this.myWorkspaces.map((w) => ({ ...w, url: companyUrl(w.slug) })),
      email: this.me?.email ?? null,
      role: this.me?.role ?? null,
      realtime: this.realtimeState,
    };
  }

  /**
   * Switches this browser to another company the user belongs to. The data itself never moves:
   * records, settings, secrets, members and files are all scoped by workspace_id and re-read
   * from scratch under the new scope, so the two companies stay completely separate.
   */
  async switchWorkspace(slug: string): Promise<boolean> {
    const session = await getSession();
    if (!session) return false;
    const ctx = await resolveWorkspace(session.user.id, this.appKey, slug);
    if (!ctx || ctx.workspace.slug !== slug) return false;
    rememberCompanySlug(slug);
    return true;
  }

  bootError(): string | null {
    return this.boot;
  }

  // ───────────────────────────── auth ─────────────────────────────

  async signIn(email: string, password: string): Promise<SignInResult> {
    let userId: string;
    try {
      const session = await signInWithPassword(email, password);
      userId = session.user.id;
    } catch (err: unknown) {
      if (err instanceof SignInError) {
        const reason = /banned/i.test(err.message) ? 'inactive' : err.reason;
        return { ok: false, reason, message: err.message };
      }
      return { ok: false, reason: 'unknown', message: errorMessage(err) };
    }

    try {
      const ctx = await resolveWorkspace(userId, this.appKey, preferredCompanySlug());
      if (!ctx) {
        await signOut();
        return { ok: false, reason: 'no_workspace', message: 'No active membership for this workspace.' };
      }
      // Hydrate here (instead of making the caller reload the page) so the
      // workspace isn't resolved twice and the browser doesn't re-fetch/re-parse
      // the whole bundle just to redo work this call already did.
      await this.hydrate(ctx);
      this.watchAuthState();
      return { ok: true, role: ctx.member.role };
    } catch (err: unknown) {
      await signOut().catch(() => undefined);
      this.ctx = null;
      return { ok: false, reason: 'network', message: errorMessage(err) };
    }
  }

  async signInWithGoogle(): Promise<void> {
    // No session to resolve here: the redirect takes the browser away from the SPA entirely,
    // and init() resolves workspace membership for the returning session on the next page load.
    await signInWithGoogle(`${window.location.origin}/`);
  }

  async signOut(): Promise<void> {
    this.realtime.stop();
    await signOut();
    this.ctx = null;
    this.cache.clear();
  }

  async sendPasswordReset(email: string): Promise<void> {
    await sendPasswordResetEmail(email, `${window.location.origin}/`);
  }

  currentUser(): LegacyUser | null {
    const me = this.me;
    return me && me.active ? this.toLegacyUser(me) : null;
  }

  users(): LegacyUser[] {
    return this.members.map((m) => this.toLegacyUser(m));
  }

  private toLegacyUser(member: WorkspaceMember): LegacyUser {
    return {
      id: member.email,
      uid: member.userId,
      email: member.email,
      name: member.displayName || member.email.split('@')[0] || member.email,
      empCode: member.empCode ?? '',
      role: member.role,
      active: member.active,
      grants: member.grants === '*' ? '*' : structuredClone(member.grants),
      menus: member.menus ? { ...member.menus } : null,
    };
  }

  private async refreshMembers(): Promise<void> {
    this.members = await listMembers(this.scope);
  }

  /** Persists name / employee link / role / grants / menu edits made in the legacy settings UI. */
  async saveUsers(users: unknown): Promise<void> {
    if (this.me?.role !== 'admin') throw new LegacyWriteError('forbidden', 'Only admins can change accounts.');
    if (!Array.isArray(users)) return;
    const scope = this.scope;

    const profileChanges: Array<{ userId: string; displayName: string; role: MemberRole; empCode: string | null; empChanged: boolean }> = [];
    const accessChanges: Array<{ userId: string; grants: ReturnType<typeof parseGrants>; menus: ReturnType<typeof parseMenus> }> = [];

    for (const raw of users) {
      if (!isJsonObject(raw)) continue;
      const prev = this.members.find((m) => m.userId === asString(raw.uid));
      if (!prev) continue;

      const displayName = asString(raw.name, prev.displayName).trim() || prev.displayName;
      const role = isRole(raw.role) ? raw.role : prev.role;
      const empCode = asString(raw.empCode).trim() || null;
      if (displayName !== prev.displayName || role !== prev.role || empCode !== prev.empCode) {
        profileChanges.push({ userId: prev.userId, displayName, role, empCode, empChanged: empCode !== prev.empCode });
      }

      const grants = parseGrants(toJson(raw.grants));
      const menus = parseMenus(toJson(raw.menus));
      const menusJson: Json = menus ? { ...menus } : null;
      const prevMenusJson: Json = prev.menus ? { ...prev.menus } : null;
      if (!jsonEqual(grantsToJson(grants), grantsToJson(prev.grants)) || !jsonEqual(menusJson, prevMenusJson)) {
        accessChanges.push({ userId: prev.userId, grants, menus });
      }
    }

    // One service-role call for profile + grants together: writing these tables directly
    // from the browser is subject to RLS, and a drifted policy made legitimate admin role
    // changes fail with "new row violates row-level security policy for member_app_access".
    const patches = new Map<string, SaveMemberInput & { userId: string }>();
    for (const change of profileChanges) {
      patches.set(change.userId, {
        userId: change.userId,
        displayName: change.displayName,
        role: change.role,
        empCode: change.empCode,
      });
    }
    for (const change of accessChanges) {
      patches.set(change.userId, {
        ...(patches.get(change.userId) ?? { userId: change.userId }),
        grants: change.grants,
        menus: change.menus,
      });
    }

    try {
      await saveMembers(scope.workspaceId, scope.appKey, [...patches.values()]);
    } finally {
      await this.refreshMembers();
    }
  }

  readonly admin = {
    createUser: async (input: unknown): Promise<{ userId: string; existingUser: boolean }> => {
      const obj = asJsonObject(toJson(input));
      const email = normalizeEmail(asString(obj.email));
      const password = asString(obj.password);
      const role = isRole(obj.role) ? obj.role : 'employee';
      if (!isValidEmail(email)) throw new LegacyWriteError('invalid', 'Enter a valid email address.');
      if (password.length < 8) throw new LegacyWriteError('invalid', 'Password must be at least 8 characters.');

      const scope = this.scope;
      try {
        const result = await createMember({
          workspaceId: scope.workspaceId,
          appKey: scope.appKey,
          email,
          password,
          displayName: asString(obj.name).trim() || email.split('@')[0] || email,
          role,
          empCode: asString(obj.empCode).trim() || null,
          grants: parseGrants(toJson(obj.grants)),
        });
        return result;
      } catch (err: unknown) {
        const mapped = toDataError(err);
        throw new LegacyWriteError(mapped.code, mapped.message);
      } finally {
        await this.refreshMembers().catch(() => undefined);
      }
    },
    /** Role + that role's permission matrix, applied together by the server. */
    setRole: async (uid: string, role: unknown): Promise<void> => {
      if (!isRole(role)) throw new LegacyWriteError('invalid', 'Unknown role.');
      const scope = this.scope;
      await this.adminCall(() => setMemberRole(scope.workspaceId, scope.appKey, uid, role));
    },
    setPassword: async (uid: string, password: string): Promise<void> => {
      if (password.length < 8) throw new LegacyWriteError('invalid', 'Password must be at least 8 characters.');
      await this.adminCall(() => setMemberPassword(this.scope.workspaceId, uid, password));
    },
    setActive: async (uid: string, active: boolean): Promise<void> => {
      await this.adminCall(() => setMemberActive(this.scope.workspaceId, uid, active));
    },
    deleteUser: async (uid: string): Promise<void> => {
      await this.adminCall(() => deleteMember(this.scope.workspaceId, uid));
    },
  };

  readonly files = {
    upload: async (module: string, file: File) => {
      try {
        return await uploadFile(this.scope, module, file);
      } catch (err: unknown) {
        const mapped = toDataError(err);
        throw new LegacyWriteError(mapped.code, mapped.message);
      }
    },
    signedUrl: async (path: string, downloadName?: string): Promise<string> => {
      try {
        return await signedFileUrl(path, downloadName);
      } catch (err: unknown) {
        const mapped = toDataError(err);
        throw new LegacyWriteError(mapped.code, mapped.message);
      }
    },
    remove: async (path: string): Promise<void> => {
      // Best-effort by design: callers (detaching a file from a task) should not fail
      // the whole operation just because the underlying object is already gone.
      await removeFile(path).catch(() => undefined);
    },
    /** Independent Storage copy for callers attaching one upload to several records — see copyFile(). */
    copy: async (module: string, source: { path: string; name: string; type: string; size: number }) => {
      try {
        return await copyFile(this.scope, module, source);
      } catch (err: unknown) {
        const mapped = toDataError(err);
        throw new LegacyWriteError(mapped.code, mapped.message);
      }
    },
  };

  private async adminCall(fn: () => Promise<void>): Promise<void> {
    try {
      await fn();
    } catch (err: unknown) {
      const mapped = toDataError(err);
      throw new LegacyWriteError(mapped.code, mapped.message);
    } finally {
      await this.refreshMembers().catch(() => undefined);
    }
  }

  // ───────────────────────────── settings ─────────────────────────────

  private localPrefs(): JsonObject {
    try {
      return asJsonObject(JSON.parse(safeStorage()?.getItem(`LUVIA_TOUR:${this.appKey}:prefs`) ?? '{}'));
    } catch {
      return {};
    }
  }

  private saveLocalPrefs(prefs: JsonObject): void {
    try {
      safeStorage()?.setItem(`LUVIA_TOUR:${this.appKey}:prefs`, JSON.stringify(prefs));
    } catch {
      // storage disabled: preference lasts for this page only
    }
  }

  /** Shared settings (+ secrets for admins) + this browser's preferences, as one CONFIG object. */
  settings(): JsonObject {
    const merged: JsonObject = structuredClone(this.settingsServer);
    for (const key of SECRET_KEYS) {
      const value = this.secretsServer[key];
      if (typeof value === 'string') merged[key] = value;
    }
    const connectorKeys = asJsonObject(this.secretsServer[CONNECTOR_KEYS_FIELD]);
    if (Array.isArray(merged.CONNECTORS)) {
      merged.CONNECTORS = merged.CONNECTORS.map((connector) => {
        if (!isJsonObject(connector)) return connector;
        const key = connectorKeys[asString(connector.id)];
        return typeof key === 'string' ? { ...connector, key } : connector;
      });
    }
    const prefs = this.localPrefs();
    if (prefs.SYNC_MODE !== 'manual' && prefs.SYNC_MODE !== 'realtime') prefs.SYNC_MODE = 'realtime';
    return { ...merged, ...prefs };
  }

  /** Called by legacy saveConfig(). Serialised so overlapping saves diff against fresh server state. */
  saveSettings(config: unknown): Promise<void> {
    const run = this.settingsQueue.then(() => this.saveSettingsNow(config));
    this.settingsQueue = run.catch(() => undefined);
    return run;
  }

  private async saveSettingsNow(config: unknown): Promise<void> {
    const all = toJsonObject(config);

    const prefs = this.localPrefs();
    for (const key of LOCAL_PREF_KEYS) {
      if (all[key] !== undefined) prefs[key] = all[key];
      delete all[key];
    }
    this.saveLocalPrefs(prefs);

    const me = this.me;
    if (!this.ctx || !me || (me.role !== 'admin' && me.role !== 'manager')) return;

    for (const key of CODE_OWNED_KEYS) delete all[key];

    const secrets: JsonObject = {};
    for (const key of SECRET_KEYS) {
      if (typeof all[key] === 'string') secrets[key] = all[key];
      delete all[key];
    }
    if (Array.isArray(all.CONNECTORS)) {
      const connectorKeys: JsonObject = {};
      all.CONNECTORS = all.CONNECTORS.map((connector) => {
        if (!isJsonObject(connector)) return connector;
        const { key, ...rest } = connector;
        if (typeof key === 'string' && key) connectorKeys[asString(connector.id)] = key;
        return rest;
      });
      secrets[CONNECTOR_KEYS_FIELD] = connectorKeys;
    }

    const scope = this.scope;
    const sharedPatch = diffTopLevel(this.settingsServer, all);
    // Only additions/changes are sent; keys the current UI doesn't know about are kept.
    const safeSharedPatch = { set: sharedPatch.set, unset: [] };
    if (!isEmptyPatch(safeSharedPatch)) {
      this.settingsServer = await patchSettings(scope, safeSharedPatch);
    }

    if (me.role === 'admin') {
      const secretsPatch = diffTopLevel(this.secretsServer, secrets);
      const safeSecretsPatch = { set: secretsPatch.set, unset: [] };
      if (!isEmptyPatch(safeSecretsPatch)) {
        this.secretsServer = await patchSecrets(scope, safeSecretsPatch);
      }
    }
  }

  private async refreshSettings(): Promise<void> {
    const scope = this.scope;
    const [settings, secrets] = await Promise.all([
      fetchSettings(scope),
      this.me?.role === 'admin' ? fetchSecrets(scope) : Promise.resolve<JsonObject>({}),
    ]);
    this.settingsServer = settings;
    this.secretsServer = secrets;
  }

  // ───────────────────────────── records ─────────────────────────────

  private moduleCache(module: string): Map<string, RecordDoc> {
    let map = this.cache.get(module);
    if (!map) {
      map = new Map();
      this.cache.set(module, map);
    }
    return map;
  }

  private toDoc(record: unknown): RecordDoc | null {
    const obj = toJsonObject(record);
    const id = asString(obj.id);
    if (!id) return null;
    const now = new Date().toISOString();
    return {
      ...obj,
      id,
      rev: typeof obj.rev === 'number' ? obj.rev : 0,
      createdAt: asString(obj.createdAt, now),
      updatedAt: asString(obj.updatedAt, now),
    };
  }

  private requireRepo(): RecordsRepository {
    if (!this.repo) throw new LegacyWriteError('unauthenticated', 'Not signed in.');
    return this.repo;
  }

  readonly records = {
    modules: (): string[] => [...this.moduleList],

    /** Synchronous read of the server-backed cache (deep copy, callers may mutate). */
    read: (module: string): RecordDoc[] => structuredClone([...this.moduleCache(module).values()]),

    put: (module: string, record: unknown): void => {
      const doc = this.toDoc(record);
      if (doc) this.moduleCache(module).set(doc.id, doc);
    },

    drop: (module: string, id: string): void => {
      this.moduleCache(module).delete(id);
    },

    /** Demo rows: added to this browser's cache only when the id is not already on the server. */
    seed: (module: string, rows: unknown): void => {
      if (!Array.isArray(rows)) return;
      const map = this.moduleCache(module);
      for (const row of rows) {
        const doc = this.toDoc(row);
        if (doc && !map.has(doc.id)) map.set(doc.id, doc);
      }
    },

    upsert: async (module: string, record: unknown): Promise<RecordDoc> => {
      const repo = this.requireRepo();
      const payload = toJsonObject(record);
      if (!asString(payload.id)) throw new LegacyWriteError('invalid', 'Record id is required.');
      let outcome;
      try {
        outcome = await repo.upsert(module, payload);
      } catch (err: unknown) {
        const mapped = toDataError(err);
        throw new LegacyWriteError(mapped.code, mapped.message);
      }
      this.moduleCache(module).set(outcome.record.id, outcome.record);
      if (outcome.status === 'conflict') {
        throw new LegacyWriteError('conflict', 'Updated by another user first.', structuredClone(outcome.record));
      }
      this.bumpCursor(module, outcome.record.updatedAt);
      return structuredClone(outcome.record);
    },

    remove: async (module: string, id: string): Promise<void> => {
      const repo = this.requireRepo();
      try {
        await repo.remove(module, id);
      } catch (err: unknown) {
        const mapped = toDataError(err);
        throw new LegacyWriteError(mapped.code, mapped.message);
      }
      this.moduleCache(module).delete(id);
    },

    /** Manual "Sync" button: applies server changes since this module's cursor. */
    pull: async (module: string): Promise<PullResult> => {
      const repo = this.requireRepo();
      const map = this.moduleCache(module);
      const delta = await repo.delta(module, this.cursors.get(module) ?? '');
      const result: PullResult = { created: 0, updated: 0, deleted: 0, conflicts: 0 };

      for (const doc of [...delta.created, ...delta.updated]) {
        const cached = map.get(doc.id);
        if (cached && cached.rev >= doc.rev) continue;
        if (cached) result.updated += 1;
        else result.created += 1;
        map.set(doc.id, doc);
      }
      for (const id of delta.deleted) {
        if (map.delete(id)) result.deleted += 1;
      }
      this.cursors.set(module, delta.cursor);
      return result;
    },

    importRows: async (module: string, rows: unknown): Promise<number> => {
      const repo = this.requireRepo();
      if (!Array.isArray(rows)) return 0;
      try {
        const count = await repo.importRows(module, rows);
        await this.reloadModule(module);
        return count;
      } catch (err: unknown) {
        const mapped = toDataError(err);
        throw new LegacyWriteError(mapped.code, mapped.message);
      }
    },

    purgeDemo: async (): Promise<number> => {
      const repo = this.requireRepo();
      const count = await repo.purgeDemo();
      for (const map of this.cache.values()) {
        for (const [id, doc] of map) if (doc.demo === true) map.delete(id);
      }
      return count;
    },

    resetAll: async (): Promise<number> => {
      const repo = this.requireRepo();
      const count = await repo.resetAll();
      this.cache.clear();
      return count;
    },
  };

  private bumpCursor(module: string, updatedAt: string): void {
    // Own writes don't advance the cursor past other users' concurrent changes:
    // keep the cursor as-is unless the module had never been synced.
    if (!this.cursors.get(module)) this.cursors.set(module, updatedAt);
  }

  private async reloadModule(module: string): Promise<void> {
    const docs = await this.requireRepo().list(module);
    const map = new Map<string, RecordDoc>();
    let cursor = this.cursors.get(module) ?? '';
    for (const doc of docs) {
      map.set(doc.id, doc);
      if (doc.updatedAt > cursor) cursor = doc.updatedAt;
    }
    this.cache.set(module, map);
    this.cursors.set(module, cursor);
  }

  // ───────────────────────────── realtime ─────────────────────────────

  readonly realtime = {
    start: (records: boolean): void => {
      this.realtime.stop();
      if (!this.ctx) return;
      const scope = this.scope;
      this.unsubscribeRealtime = subscribeWorkspace(
        scope,
        {
          onRecord: (change) => {
            void this.applyRecordChange(change.module, change.id, change.deleted, change.updatedBy);
          },
          onSettings: () => {
            this.refreshSettings()
              .then(() => this.emit({ kind: 'settings' }))
              .catch((err: unknown) => console.warn('[LuviaCore] settings refresh failed', err));
          },
          onMembers: () => {
            const myId = this.ctx?.member.userId;
            this.refreshMembers()
              .then(() => {
                const me = this.members.find((m) => m.userId === myId);
                this.emit({ kind: 'members', selfRemoved: !me || !me.active });
              })
              .catch((err: unknown) => console.warn('[LuviaCore] members refresh failed', err));
          },
          onStatus: (status) => {
            const reconnected = status === 'live' && this.realtimeWasLive && this.realtimeState !== 'live';
            this.realtimeState = status;
            if (status === 'live') this.realtimeWasLive = true;
            this.emit({ kind: 'realtime', status });
            if (reconnected) void this.resyncAfterReconnect();
          },
        },
        { records, config: true },
      );
    },
    stop: (): void => {
      this.unsubscribeRealtime?.();
      this.unsubscribeRealtime = null;
      this.realtimeState = 'idle';
      this.realtimeWasLive = false;
    },
    status: (): RealtimeStatus => this.realtimeState,
  };

  private async applyRecordChange(module: string, id: string, deleted: boolean, updatedBy: string | null): Promise<void> {
    if (!this.repo || !this.moduleList.includes(module)) return;
    const map = this.moduleCache(module);
    const cached = map.get(id);
    const byMe = updatedBy !== null && updatedBy === this.ctx?.member.userId;

    try {
      if (deleted) {
        if (!map.delete(id)) return;
        this.emit({ kind: 'record', module, id, change: 'deleted', byMe });
        return;
      }
      const { record } = await this.repo.get(module, id);
      if (!record) {
        if (map.delete(id)) this.emit({ kind: 'record', module, id, change: 'deleted', byMe });
        return;
      }
      if (cached && cached.rev >= record.rev) return; // echo of our own write
      map.set(id, record);
      this.emit({ kind: 'record', module, id, change: cached ? 'updated' : 'created', byMe });
    } catch (err: unknown) {
      console.warn('[LuviaCore] realtime record refresh failed', err);
    }
  }

  private async resyncAfterReconnect(): Promise<void> {
    try {
      await Promise.all(this.moduleList.map((module) => this.records.pull(module)));
      await Promise.all([this.refreshSettings(), this.refreshMembers()]);
      this.emit({ kind: 'resync' });
    } catch (err: unknown) {
      console.warn('[LuviaCore] resync after reconnect failed', err);
    }
  }

  // ───────────────────────────── audit ─────────────────────────────

  readonly audit = {
    rows: (): LegacyAuditRow[] => structuredClone(this.auditRows),
    push: (row: unknown): Promise<number | undefined> => {
      if (!this.ctx) return Promise.resolve(undefined);
      const obj = toJsonObject(row);
      const me = this.me;
      return insertAudit(this.scope, {
        action: asString(obj.action, 'event'),
        module: obj.module,
        targetId: obj.targetId,
        label: obj.label,
        before: obj.before,
        after: obj.after,
        actorEmail: me?.email ?? null,
        actorName: me?.displayName ?? null,
      }).catch((err: unknown) => {
        console.warn('[LuviaCore] audit insert failed', err);
        return undefined;
      });
    },
    remove: async (ids: number[]): Promise<void> => {
      if (this.me?.role !== 'admin') throw new LegacyWriteError('forbidden', 'Only admins can delete change-history records.');
      try {
        await deleteAudit(this.scope, ids);
      } catch (err: unknown) {
        const mapped = toDataError(err);
        throw new LegacyWriteError(mapped.code, mapped.message);
      }
      const removed = new Set(ids);
      this.auditRows = this.auditRows.filter((row) => !removed.has(row.id));
    },
  };

  async ping(): Promise<number> {
    const started = performance.now();
    const { error } = await getSupabase().from('apps').select('app_key').limit(1);
    if (error) throw toDataError(error);
    return Math.round(performance.now() - started);
  }

  // ─────────────── migration of data saved by the pre-Supabase build ───────────────

  private legacyKey(name: string): string {
    return `LUVIA_TOUR:${this.appKey}:${name}`;
  }

  /** Plaintext passwords and the old fake session must not linger in the browser. */
  private purgeLegacySecurityKeys(): void {
    const storage = safeStorage();
    if (!storage) return;
    for (const name of LEGACY_SECURITY_KEYS) {
      storage.removeItem(this.legacyKey(name));
      try {
        window.sessionStorage.removeItem(this.legacyKey(name));
      } catch {
        // sessionStorage unavailable
      }
    }
  }

  legacyLocalData(): LegacyLocalData | null {
    const storage = safeStorage();
    if (!storage || !this.ctx) return null;

    const modules: Record<string, JsonObject[]> = {};
    let count = 0;
    for (const module of this.moduleList) {
      const raw = storage.getItem(this.legacyKey(module));
      if (!raw) continue;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) continue;
        const rows = parsed.map((row) => toJson(row)).filter(isJsonObject).filter((row) => row.demo !== true);
        if (rows.length > 0) {
          modules[module] = rows;
          count += rows.length;
        }
      } catch {
        // unreadable leftovers are ignored
      }
    }

    let config: JsonObject | null = null;
    if (this.me?.role === 'admin') {
      try {
        const parsed = asJsonObject(JSON.parse(storage.getItem(this.legacyKey('config')) ?? 'null'));
        if (Object.keys(parsed).length > 0) {
          config = parsed;
          count += 1;
        }
      } catch {
        config = null;
      }
    }

    return count > 0 ? { count, config, modules } : null;
  }

  /**
   * Uploads work data an earlier build stored only in this browser. Existing server rows
   * are never overwritten; admins additionally merge the old settings into the shared config.
   */
  async importLegacyLocalData(): Promise<number> {
    const data = this.legacyLocalData();
    if (!data) return 0;
    const repo = this.requireRepo();
    let imported = 0;

    if (data.config && this.me?.role === 'admin') {
      const current = this.settings();
      const merged: JsonObject = { ...current };
      for (const [key, value] of Object.entries(data.config)) {
        if (value === undefined || (CODE_OWNED_KEYS as readonly string[]).includes(key)) continue;
        if (key === 'MENUS' && isJsonObject(value)) merged.MENUS = { ...asJsonObject(current.MENUS), ...value };
        else merged[key] = value;
      }
      await this.saveSettings(merged);
      imported += 1;
    }

    for (const [module, rows] of Object.entries(data.modules)) {
      const map = this.moduleCache(module);
      for (const row of rows) {
        const id = asString(row.id);
        if (!id || map.has(id)) continue;
        const { rev: _rev, createdAt: _c, updatedAt: _u, ...body } = row;
        try {
          const outcome = await repo.upsert(module, body);
          map.set(outcome.record.id, outcome.record);
          if (outcome.status === 'ok') imported += 1;
        } catch (err: unknown) {
          const mapped = toDataError(err);
          if (mapped.code !== 'forbidden') throw new LegacyWriteError(mapped.code, mapped.message);
        }
      }
    }

    this.clearLegacyLocalData();
    return imported;
  }

  clearLegacyLocalData(): void {
    const storage = safeStorage();
    if (!storage) return;
    for (const module of this.moduleList) storage.removeItem(this.legacyKey(module));
    for (const name of LEGACY_DATA_KEYS) storage.removeItem(this.legacyKey(name));
  }
}

export async function createLuviaCore(): Promise<LuviaCoreApi> {
  const core = new LegacyCore();
  await core.init();
  return core;
}
